/**
 * Scheduled Appointments Page
 * Displays virtual and in-person appointments in tabbed view with pagination and search
 * - EA/ED: Can see all appointments and both tabs
 * - ES/CS/PE: Can only see their own virtual appointments
 */

import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { RefreshCw, Video, Building } from 'lucide-react';
import { useScheduledAppointments } from '@/hooks/appointments/useScheduledAppointments';
import { ScheduledAppointmentsTable } from '@/components/appointments/ScheduledAppointmentsTable';
import { ScheduledAppointmentsSearchForm, AppointmentSearchFilters } from '@/components/appointments/ScheduledAppointmentsSearchForm';
import PaginationComponent from '@/components/common/PaginationComponent';

const ScheduledAppointmentsPage: React.FC = () => {
  useEffect(() => {
    document.title = "Scheduled Appointments | Admin";
  }, []);
  const {
    virtualAppointments,
    inPersonAppointments,
    isLoading,
    error,
    refetch,
    canViewInPerson,
    // Virtual pagination
    virtualPage,
    virtualTotalPages,
    virtualTotalCount,
    virtualRecordsPerPage,
    setVirtualPage,
    setVirtualRecordsPerPage,
    // In-person pagination
    inPersonPage,
    inPersonTotalPages,
    inPersonTotalCount,
    inPersonRecordsPerPage,
    setInPersonPage,
    setInPersonRecordsPerPage,
    // Search
    virtualFilters,
    inPersonFilters,
    searchVirtual,
    searchInPerson,
    resetVirtualSearch,
    resetInPersonSearch,
  } = useScheduledAppointments();

  const [activeTab, setActiveTab] = useState('virtual');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  const handleVirtualSearch = (filters: AppointmentSearchFilters) => {
    searchVirtual(filters);
  };

  const handleInPersonSearch = (filters: AppointmentSearchFilters) => {
    searchInPerson(filters);
  };

  // Calculate start and end indices for display
  const virtualStartIndex = virtualTotalCount > 0 ? (virtualPage - 1) * virtualRecordsPerPage + 1 : 0;
  const virtualEndIndex = Math.min(virtualPage * virtualRecordsPerPage, virtualTotalCount);

  const inPersonStartIndex = inPersonTotalCount > 0 ? (inPersonPage - 1) * inPersonRecordsPerPage + 1 : 0;
  const inPersonEndIndex = Math.min(inPersonPage * inPersonRecordsPerPage, inPersonTotalCount);

  return (
    <div id="scheduled_appointments_page_container" className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 id="scheduled_appointments_page_title" className="text-3xl font-bold text-foreground">Scheduled Appointments</h1>
          <p className="text-muted-foreground mt-2">
            View and manage scheduled patient appointments
          </p>
        </div>
        <Button
          id="scheduled_appointments_refresh_btn"
          onClick={handleRefresh}
          variant="outline"
          disabled={isRefreshing || isLoading}
          className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div id="scheduled_appointments_error" className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg border border-destructive/20">
          {error}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList id="scheduled_appointments_tabs_list" className="grid w-fit grid-cols-2 bg-transparent p-0 h-auto">
          <TabsTrigger
            id="scheduled_appointments_tab_virtual"
            value="virtual"
            className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none pb-4 px-6 flex items-center gap-2"
          >
            <Video className="h-4 w-4" />
            Virtual
            <span className="ml-1 text-xs bg-muted px-2 py-0.5 rounded-full">
              {virtualTotalCount}
            </span>
          </TabsTrigger>
          {canViewInPerson && (
            <TabsTrigger
              id="scheduled_appointments_tab_in_person"
              value="in-person"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none pb-4 px-6 flex items-center gap-2"
            >
              <Building className="h-4 w-4" />
              In-Person
              <span className="ml-1 text-xs bg-muted px-2 py-0.5 rounded-full">
                {inPersonTotalCount}
              </span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent id="scheduled_appointments_tab_content_virtual" value="virtual" className="mt-6 space-y-4">
          {/* Virtual Search Form */}
          <ScheduledAppointmentsSearchForm
            onSearch={handleVirtualSearch}
            onReset={resetVirtualSearch}
            isLoading={isLoading}
            type="virtual"
            currentFilters={virtualFilters}
          />
          
          <ScheduledAppointmentsTable
            appointments={virtualAppointments}
            isLoading={isLoading}
            type={'virtual'}
            emptyMessage="No virtual appointments scheduled"
          />
          {virtualTotalCount > 0 && (
            <PaginationComponent
              currentPage={virtualPage}
              totalPages={virtualTotalPages}
              recordsPerPage={virtualRecordsPerPage}
              totalRecords={virtualTotalCount}
              startIndex={virtualStartIndex}
              endIndex={virtualEndIndex}
              onPageChange={setVirtualPage}
              onRecordsPerPageChange={setVirtualRecordsPerPage}
            />
          )}
        </TabsContent>

        {canViewInPerson && (
          <TabsContent id="scheduled_appointments_tab_content_in_person" value="in-person" className="mt-6 space-y-4">
            {/* In-Person Search Form */}
            <ScheduledAppointmentsSearchForm
              onSearch={handleInPersonSearch}
              onReset={resetInPersonSearch}
              isLoading={isLoading}
              type="in-person"
              currentFilters={inPersonFilters}
            />
            
            <ScheduledAppointmentsTable
              appointments={inPersonAppointments}
              isLoading={isLoading}
              type={'in-person'}
              emptyMessage="No in-person appointments scheduled"
            />
            {inPersonTotalCount > 0 && (
              <PaginationComponent
                currentPage={inPersonPage}
                totalPages={inPersonTotalPages}
                recordsPerPage={inPersonRecordsPerPage}
                totalRecords={inPersonTotalCount}
                startIndex={inPersonStartIndex}
                endIndex={inPersonEndIndex}
                onPageChange={setInPersonPage}
                onRecordsPerPageChange={setInPersonRecordsPerPage}
              />
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default ScheduledAppointmentsPage;
